import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { BookOpen, Search, Eye, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';

const CATEGORY_COLORS = {
  setup:          'bg-blue-600',
  troubleshooting:'bg-red-600',
  materials:      'bg-green-600',
  technique:      'bg-purple-600',
  calibration:    'bg-yellow-600',
  safety:         'bg-orange-600',
};

const Print3DKnowledge = () => {
  const [articles, setArticles] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTags, setExpandedTags] = useState({});
  const debounceRef = useRef(null);

  useEffect(() => {
    fetchArticles();
    axios.get(`${API}/printing3d/article-categories`)
      .then(res => setCategories(res.data || []))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchArticles = async (search = '', category = activeCategory) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (category && category !== 'all') params.set('category', category);
      const res = await axios.get(`${API}/printing3d/articles?${params}`);
      setArticles(res.data || []);
    } catch (err) {
      console.error('Failed to load articles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    clearTimeout(debounceRef.current);
    fetchArticles(searchTerm, activeCategory);
  };

  const handleCategoryChange = (cat) => {
    setActiveCategory(cat);
    fetchArticles(searchTerm, cat);
  };

  const handleArticleClick = async (article) => {
    try {
      const res = await axios.get(`${API}/printing3d/articles/${article.id}`);
      setSelectedArticle(res.data);
    } catch {
      setSelectedArticle(article);
    }
  };

  if (selectedArticle) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="print3d-article-detail" style={bgStyle(IMAGES.PRINTING3D_KNOWLEDGE)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => setSelectedArticle(null)}
            className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Knowledge Base
          </button>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-1 rounded text-xs font-bold uppercase text-white ${CATEGORY_COLORS[selectedArticle.category] || 'bg-zinc-600'}`}>
                  {selectedArticle.category}
                </span>
                <span className="flex items-center gap-1 text-zinc-500 text-xs">
                  <Eye className="w-3 h-3" /> {selectedArticle.views} views
                </span>
              </div>
              <CardTitle className="text-white text-2xl md:text-3xl">{selectedArticle.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap mb-6">
                {selectedArticle.content}
              </div>
              <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800">
                {selectedArticle.tags?.map((tag, i) => (
                  <span key={i} className="px-2 py-1 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 text-center">
            <button
              onClick={() => setSelectedArticle(null)}
              className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mx-auto"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Knowledge Base
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" data-testid="print3d-knowledge" style={bgStyle(IMAGES.PRINTING3D_KNOWLEDGE)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <Link to="/3d-printing" className="flex items-center gap-2 text-zinc-400 hover:text-white mb-4 transition-colors w-fit">
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <BookOpen className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">LEARN</div>
            <h1 className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Knowledge Base
            </h1>
            <p className="text-sm text-zinc-300 mt-1">
              Guides, techniques, calibration, safety, and troubleshooting articles
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <Card className="bg-zinc-900 border-zinc-800 mb-5">
        <CardContent className="pt-4 pb-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                type="text"
                placeholder="Search articles, tags, or topics..."
                value={searchTerm}
                onChange={(e) => {
                  const val = e.target.value;
                  setSearchTerm(val);
                  clearTimeout(debounceRef.current);
                  debounceRef.current = setTimeout(() => fetchArticles(val, activeCategory), 300);
                }}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-9 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>
            <Button
              onClick={handleSearch}
              className="bg-orange-500 hover:bg-orange-600 text-black font-bold"
            >
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Category filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        {['all', ...categories].map(cat => (
          <button
            key={cat}
            onClick={() => handleCategoryChange(cat)}
            className={`px-3 py-1.5 rounded text-xs font-semibold uppercase transition-colors ${
              activeCategory === cat
                ? 'bg-orange-500 text-black'
                : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
            }`}
          >
            {cat === 'all' ? 'All Topics' : cat}
          </button>
        ))}
      </div>

      {/* Article grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <BookOpen className="w-10 h-10 text-orange-500 animate-pulse" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {articles.map((article, i) => (
            <Card
              key={article.id}
              onClick={() => handleArticleClick(article)}
              className="bg-zinc-900 border-zinc-800 hover:border-orange-500/50 transition-colors cursor-pointer group"
            >
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2 mb-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-bold uppercase text-white ${CATEGORY_COLORS[article.category] || 'bg-zinc-600'}`}>
                    {article.category}
                  </span>
                  <span className="flex items-center gap-1 text-zinc-500 text-xs">
                    <Eye className="w-3 h-3" /> {article.views}
                  </span>
                </div>
                <CardTitle className="text-white text-base group-hover:text-orange-400 transition-colors leading-snug">
                  {article.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-zinc-400 text-xs line-clamp-3 mb-3 leading-relaxed">
                  {article.content?.slice(0, 180)}...
                </p>
                <div className="flex flex-wrap gap-1">
                  {article.tags?.slice(0, 3).map((tag, j) => (
                    <span key={j} className="px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded text-xs">
                      #{tag}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!loading && articles.length === 0 && (
        <div className="text-center py-16 text-zinc-500">
          <BookOpen className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p>No articles found.</p>
        </div>
      )}
    </div>
  );
};

export default Print3DKnowledge;
